<template lang="pug">
    .ma-sm-2.px-4
        template(v-if="isLoading")
            .text-center
                .d-flex.justify-center.align-center(style="height: 100%;")
                    v-progress-circular(
                        :size='100'
                        :width='10'
                        color='var(--volie-blue)'
                        indeterminate
                    )
        template(v-else-if="llmAssessments?.length > 0")
            v-card.mx-auto.mt-md-4.mb-md-4(
                v-for="llmAssessment in llmAssessments" 
                :key="llmAssessment.llm_assessment_id" 
                :class="isResponsibleForEnrollment(llmAssessment) ? 'pulse-enrollment-responsible-assessment' : ''"
                outlined
            )
                v-card-text
                    template(v-if="isResponsibleForEnrollment(llmAssessment)")
                        v-chip.mb-2(outlined color="var(--error)")
                            | REASON FOR ENROLLMENT
                    div.d-flex.justify-space-between.align-center
                        .d-flex.flex-column
                            FlaggedOrApprovedText(
                                :scorecardOrLlmAssessment="llmAssessment"
                                itemType="llm_assessment"
                            )
                            div.d-flex.align-center.mt-1
                                template(v-if="llmAssessment.escalated_customer_id > 0")
                                    v-tooltip(bottom)
                                        template(v-slot:activator="{ on, attrs }")
                                            v-icon(color="var(--error)" v-bind="attrs" v-on="on")
                                                | mdi-fire-alert
                                        span Customer Escalated
                                template(v-if="llmAssessment.appointment_opportunity_status && llmAssessment.appointment_opportunity_status !== ''")
                                    v-tooltip(bottom)
                                        template(v-slot:activator="{ on, attrs }")
                                            v-icon(:color="llmAssessment.appointment_opportunity_status === 'missed' ? 'error' : 'var(--volie-blue)'" v-bind="attrs" v-on="on")
                                                | mdi-hand-coin-outline
                                        span Opportunity: {{ llmAssessment.appointment_opportunity_status | titleize }}
                        div.communication-info
                            div.d-flex.align-center.justify-end
                                v-icon(size="24")
                                    | {{ callIcon(llmAssessment) }}
                                | {{ llmAssessment.communication_direction | titleize }}
                            | {{ llmAssessment.friendly_communication_at }}
                    v-alert(dense border='left' color="var(--off-white)" icon="mdi-information").mb-0.mt-3
                        | {{ llmAssessment.llm_assessment_item_summary }}
                    template(v-if="llmAssessmentAiCommunicationDispositions(llmAssessment).length > 0")
                        PulseDispositionComparison.mt-3(
                            :agent-disposition="llmAssessment.agent_disposition_description"
                            :agent-notes="llmAssessment.agent_notes"
                            :ai-communication-dispositions="llmAssessmentAiCommunicationDispositions(llmAssessment)"
                            :conference-vid="llmAssessment.conference_vid"
                            compact
                        )
                    template(v-if="currentUserIsManagerOrAdmin")
                        router-link.btn-link(:to="{ name: 'llm_assessments_show', params: { vid: llmAssessment.vid } }" target="_blank")
                            span.mr-1 View Full Assessment 
                            v-icon(size="16" color="var(--volie-blue)")
                                | mdi-open-in-new

            ShowMoreRowNew(
                :handle-show-more="handleShowMore"
                :loading="isShowMoreLoading"
                :show-more="showMore"
            )
        v-alert(v-if="!isLoading && llmAssessments?.length === 0" :type="errorMessage ? 'error' : 'info'" outlined dense border='left') 
            | {{ errorMessage || 'No Pulse assessments found for this customer.' }}
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components
import FlaggedOrApprovedText from '@/components/shared/pulse/flagged_or_approved_text.vue';
import PulseDispositionComparison from '@/components/shared/pulse/pulse_disposition_comparison.vue';
import ShowMoreRowNew from '@/components/shared/show_more_row_new.vue';

// Data
import { GetLlmAssessments } from '@/data';

// Helpers
import { buildAiCommunicationDispositions } from '@/helpers/pulse';
import { isShowMore } from '@/helpers/show_more';

export default Vue.extend({
    data() {
        return {
            errorMessage: null,
            isLoading: false,
            isShowMoreLoading: false,
            limit: 25,
            llmAssessments: [],
            managerOrAdminRoles: [1, 2, 4, 5, 8],
            offset: 0,
            showMore: false,
        }
    },
    methods: {
        callIcon(llmAssessment: any) {
            if (!llmAssessment) {
                return;
            }

            return llmAssessment?.communication_direction === 'inbound' ? 'mdi-phone-incoming' : 'mdi-phone-outgoing';
        },
        getLlmAssessments() {
            if (!this.currentCampaignCustomer) {
                return;
            }

            const options = {
                customer_id: this.currentCampaignCustomer?.customer_id || 0,
                skip_count: true,
                include_summary: true,
                limit: this.limit || 25,
                offset: this.offset || 0,
                valid_for_analytics: true,
            }
            
            this.errorMessage = null;

            if (this.llmAssessments?.length > 0) {
                this.isShowMoreLoading = true;
            } else {
                this.isLoading = true;
            }

            GetLlmAssessments(options).then((response) => {
                if (response?.Data?.llm_assessments?.length > 0) {
                    if (this.llmAssessments?.length > 0) {
                        this.llmAssessments = this.llmAssessments.concat(response.Data.llm_assessments);
                    } else {
                        this.llmAssessments = response.Data.llm_assessments;
                    }
                }

                this.showMore = isShowMore(response?.Data?.llm_assessments?.length, this.limit);

            }).catch(() => {
                this.errorMessage = "Unable to get Pulse assessments";
                this.llmAssessments = [];
                this.showMore = false;
            }).finally(() => {
                this.isLoading = false;
                this.isShowMoreLoading = false;
            });
        },
        handleShowMore() {
            this.offset += this.limit;
            this.getLlmAssessments();
            return;
        },
        isResponsibleForEnrollment(llmAssessment) {
            if (!llmAssessment) {
                return false;
            }

            return this.currentCampaignCustomer?.eventable_id === llmAssessment.llm_assessment_id;
        },
        llmAssessmentAiCommunicationDispositions(llmAssessment) {
            return buildAiCommunicationDispositions(llmAssessment, {
                dispositionDescription: 'ai_disposition_description',
                id: 'ai_communication_disposition_id',
                notes: 'ai_disposition_notes',
            });
        },
    },
    computed: {
        currentUser() {
            return VolieState.currentUser();
        },
        currentUserIsManagerOrAdmin() {
            return this.managerOrAdminRoles.includes(this.currentUser?.activated_role_id || 0);
        },
    },
    components: {
        FlaggedOrApprovedText,
        PulseDispositionComparison,
        ShowMoreRowNew,
    },
    props: {
        currentCampaignCustomer: {
            type: Object,
            required: true,
        },
    },
    watch: {
    },
    created() {
    },
    destroyed() {
    },
    mounted() {
        this.getLlmAssessments();
    },
})
</script>

<style lang="scss" scoped>
    .communication-info {
        display: flex;
        flex-direction: column;

        > * {
            gap: 8px;
        }
    }

    .btn-link {
        display: flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        padding: 4px 8px;
        border-radius: 4px;
        font-weight: 500;
        font-size: 12px;
        text-transform: uppercase;
        text-decoration: none;
        margin-top: 8px;
        transition: all 0.3s ease;

        &:hover {
            background-color: var(--off-white);
        }
    }

    .pulse-enrollment-responsible-assessment {
        background-color: var(--volie-teal-10);
        border: 2px solid var(--pulse-green);
    }
</style>